import React, { useEffect } from 'react';
import { ArrowRightIcon, MailIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { useEnquiryModal } from '../../contexts/EnquiryModalContext';
import { contact } from '../../data/contact';
import type { SectionBg } from '../ui/Section';
interface EnquirySectionProps {
  title: string;
  description: string;
  submitLabel: string;
  bg?: SectionBg;
  defaultCourse?: string;
  defaultLocation?: string;
  showCourseFields?: boolean;
  messageLabel?: string;
}
const bgClass: Partial<Record<SectionBg, string>> = {
  white: 'bg-white',
  grey: 'bg-mist',
  cream: 'bg-cream'
};

// End-of-page call to action. The form itself lives in the enquiry popup.
export function EnquirySection({
  title,
  description,
  submitLabel,
  bg = 'cream',
  defaultCourse,
  defaultLocation,
  showCourseFields,
  messageLabel
}: EnquirySectionProps) {
  const {
    open,
    setPageConfig
  } = useEnquiryModal();
  useEffect(() => {
    setPageConfig({
      title,
      description,
      submitLabel,
      defaultCourse,
      defaultLocation,
      showCourseFields,
      messageLabel
    });
    return () => setPageConfig(null);
  }, [title, description, submitLabel, defaultCourse, defaultLocation, showCourseFields, messageLabel, setPageConfig]);
  return <section id="enquire" aria-labelledby="enquire-title" className="w-full bg-white px-2 py-2 md:px-4" data-id="element-393" data-mp="xcdol0f1ifdl">
      <div className={`rounded-[28px] md:rounded-[40px] ${bgClass[bg] ?? 'bg-cream'}`} data-id="element-394" data-mp="12b8gcruzje9u">
        <div className="mx-auto flex max-w-content flex-col gap-10 px-5 py-14 md:px-8 md:py-20 lg:flex-row lg:items-end lg:justify-between" data-id="element-395" data-mp="nmkoowo9fop6">
          <div className="max-w-3xl" data-id="element-396" data-mp="sgghzltkwne0">
            <h2 id="enquire-title" className="type-h2 text-ink" data-id="element-397" data-mp="enh4fpdcx393">
              {title}
            </h2>
            <p className="mt-4 type-body text-ink" data-id="element-398" data-mp="jlik00bkqkft">{description}</p>
            <button type="button" onClick={() => open()} className="group mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-[15px] bg-brand px-6 py-2.5 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover active:bg-brand-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-399" data-mp="jjnyokwe4qb7">
              {submitLabel}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-400" data-mp="3czlrpw5tvl0h" />
            </button>
          </div>
          <ul className="flex flex-col gap-3 lg:min-w-[300px]" aria-label="Other ways to contact us" data-id="element-401" data-mp="9in3d9vctag7">
            <ContactRow href={contact.whatsappLink} external icon={<MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-403" data-mp="4jsd5mv24jm2" />} label="WhatsApp" value={contact.whatsappDisplay} data-id="element-402" data-mp="4ih8lktsf7tw" />
            <ContactRow href={contact.emailLink} icon={<MailIcon className="h-5 w-5" aria-hidden="true" data-id="element-405" data-mp="3qp758r0uz814" />} label="Email" value={contact.email} data-id="element-404" data-mp="3qq8p9kzur4uz" />
            <ContactRow href={contact.phoneLink} icon={<PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-407" data-mp="2k7sco81i95kv" />} label="Emergency calls" value={contact.phoneDisplay} data-id="element-406" data-mp="2kay1dm6825g2" />
          </ul>
        </div>
      </div>
    </section>;
}
function ContactRow({
  href,
  icon,
  label,
  value,
  external
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  external?: boolean;
}) {
  return <li data-id="element-408" data-mp="14q5tvrq4p8q2">
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className="flex items-center gap-4 rounded-[18px] bg-white p-3 transition-colors duration-150 hover:bg-mist" data-id="element-409" data-mp="14qf8kj5q3tgg">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-cream text-brand-press" data-id="element-410" data-mp="1iiloc44yryfd">{icon}</span>
        <span className="min-w-0" data-id="element-411" data-mp="1nhf0nt8iljdy">
          <span className="block type-small text-body" data-id="element-412" data-mp="1nihrk15u7ltv">{label}</span>
          <span className="block break-all font-bold text-ink" data-id="element-413" data-mp="1sfqesg5ueg1z">{value}</span>
        </span>
      </a>
    </li>;
}